import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsButton, BdsIcon, BdsTooltip } from '@/core/constants/webComponents';
import { CARD_POST_EVENTS } from '@/features/publication-wall/constants/events';
import {
  type NormalizedSentiment,
  type Gender,
  type PostCardActionType,
  GENDER_ICONS,
  POST_CARD_ACTION_ICONS,
} from '@/core/constants/domainMappings';

type ClassifyType = Gender;
type SentimentType = NormalizedSentiment;
type ActionsType = PostCardActionType;

interface PostCardAdvancedOptionsProps {
  channel: string;
  uid: string;
  postUrl: string;
  postPid: string;
  initialChecked?: boolean;
  initialClassify?: ClassifyType;
  initialSentiment?: SentimentType;
}

const SENTIMENT_ICONS: Record<SentimentType, string> = {
  neutral: 'emoji-neutral',
  positive: 'emoji',
  negative: 'emoji-negative',
};

const SENTIMENT_COLORS: Record<SentimentType, string> = {
  positive: '#01723E',
  negative: '#8A0000',
  neutral: '#282828',
};

const UNCLASSIFIED_SENTIMENT_COLOR = '#a7a7a7';

function dispatch(type: string, detail: Record<string, unknown>) {
  document.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
}

export const PostCardAdvancedOptions: React.FC<PostCardAdvancedOptionsProps> = ({
  channel,
  uid,
  postUrl,
  postPid,
  initialChecked = false,
  initialClassify = 'man',
  initialSentiment,
}) => {
  const { t } = useTranslation();

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<'classify' | 'sentiment' | 'actions' | null>(null);
  const [classifySelection, setClassifySelection] = useState<ClassifyType>(initialClassify);
  const [sentimentSelection, setSentimentSelection] = useState<SentimentType | undefined>(initialSentiment);
  const [actionsSelection, setActionsSelection] = useState<ActionsType>('copy-id');
  const [isChecked, setIsChecked] = useState(initialChecked);

  const advancedRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setClassifySelection(initialClassify); }, [initialClassify]);
  useEffect(() => { setSentimentSelection(initialSentiment); }, [initialSentiment]);
  useEffect(() => { setIsChecked(initialChecked); }, [initialChecked]);

  useEffect(() => {
    if (!openDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (advancedRef.current && !advancedRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const handleClassify = (type: ClassifyType) => {
    setClassifySelection(type);
    setOpenDropdown(null);
    const detail: Record<string, unknown> = { postId: postPid, type, channel };
    if (uid) detail.uid = uid;
    dispatch(CARD_POST_EVENTS.CLASSIFY, detail);
  };

  const handleSentiment = (value: SentimentType) => {
    setSentimentSelection(value);
    setOpenDropdown(null);
    dispatch(CARD_POST_EVENTS.SENTIMENT, { postId: postPid, value, channel });
  };

  const handleCheck = () => {
    const newValue = !isChecked;
    setIsChecked(newValue);
    dispatch(CARD_POST_EVENTS.CHECK, { postId: postPid, channel, value: newValue });
  };

  const handleView = () => {
    dispatch(CARD_POST_EVENTS.VIEW, { postId: postPid, postUrl, channel });
    if (postUrl) window.open(postUrl, '_blank', 'noopener,noreferrer');
  };

  const handleActions = (type: ActionsType) => {
    setActionsSelection(type);
    setOpenDropdown(null);
    dispatch(CARD_POST_EVENTS.ACTIONS, { postId: postPid, type, postUrl, channel });
  };

  const handleDelete = () => {
    dispatch(CARD_POST_EVENTS.DELETE, { postId: postPid, channel });
  };

  const handleBanUser = () => {
    dispatch(CARD_POST_EVENTS.BAN_USER, { postId: postPid, uid, channel });
  };

  return (
    <>
      <BdsButton
        class="post-card__advanced-toggle"
        variant="text"
        color="content"
        icon-right={showAdvanced ? 'arrow-up' : 'arrow-down'}
        size="short"
        onClick={() => setShowAdvanced((v) => !v)}
      >
        {showAdvanced ? t('cardPosts.hideAdvancedOptions') : t('cardPosts.showAdvancedOptions')}
      </BdsButton>

      <div
        className={`post-card__advanced${showAdvanced ? ' post-card__advanced--show' : ''}`}
        ref={advancedRef}
      >
        <div className="post-card__btn-group">
          {/* Classify */}
          <div className="post-card__group-item">
            <BdsTooltip position="top-center" tooltip-text={t('cardPosts.classify')}>
              <button className="post-card__group-btn post-card__group-btn--first post-card__group-btn--wide" aria-label={t('cardPosts.classify')} onClick={() => setOpenDropdown((p) => (p === 'classify' ? null : 'classify'))}>
                <BdsIcon theme="outline" name={GENDER_ICONS[classifySelection]} size="small" />
                <BdsIcon theme="outline" name={openDropdown === 'classify' ? 'arrow-up' : 'arrow-down'} size="x-small" />
              </button>
            </BdsTooltip>
            {openDropdown === 'classify' && (
              <div className="post-card__dropdown">
                <BdsTooltip position="right-center" tooltip-text={t('cardPosts.classifyPerson')}>
                  <button className="post-card__dropdown-item" aria-label={t('cardPosts.classifyPerson')} onClick={() => handleClassify('man')}><BdsIcon theme="outline" name="user-default" size="small" /></button>
                </BdsTooltip>
                <BdsTooltip position="right-center" tooltip-text={t('cardPosts.classifyWoman')}>
                  <button className="post-card__dropdown-item" aria-label={t('cardPosts.classifyWoman')} onClick={() => handleClassify('woman')}><BdsIcon theme="outline" name="user-woman" size="small" /></button>
                </BdsTooltip>
                <BdsTooltip position="right-center" tooltip-text={t('cardPosts.classifyCompany')}>
                  <button className="post-card__dropdown-item" aria-label={t('cardPosts.classifyCompany')} onClick={() => handleClassify('company')}><BdsIcon theme="outline" name="company" size="small" /></button>
                </BdsTooltip>
              </div>
            )}
          </div>

          {/* Sentiment */}
          <div className="post-card__group-item">
            <BdsTooltip position="top-center" tooltip-text={t('cardPosts.sentiment')}>
              <button className="post-card__group-btn post-card__group-btn--wide" aria-label={t('cardPosts.sentiment')} onClick={() => setOpenDropdown((p) => (p === 'sentiment' ? null : 'sentiment'))}>
                <BdsIcon
                  theme="outline"
                  name={sentimentSelection ? SENTIMENT_ICONS[sentimentSelection] : 'emoji'}
                  size="small"
                  color={sentimentSelection ? SENTIMENT_COLORS[sentimentSelection] : UNCLASSIFIED_SENTIMENT_COLOR}
                />
                <BdsIcon theme="outline" name={openDropdown === 'sentiment' ? 'arrow-up' : 'arrow-down'} size="x-small" />
              </button>
            </BdsTooltip>
            {openDropdown === 'sentiment' && (
              <div className="post-card__dropdown">
                <BdsTooltip position="right-center" tooltip-text={t('cardPosts.sentimentPositive')}>
                  <button className="post-card__dropdown-item" aria-label={t('cardPosts.sentimentPositive')} onClick={() => handleSentiment('positive')}><BdsIcon theme="outline" name="emoji" size="small" color={SENTIMENT_COLORS.positive} /></button>
                </BdsTooltip>
                <BdsTooltip position="right-center" tooltip-text={t('cardPosts.sentimentNegative')}>
                  <button className="post-card__dropdown-item" aria-label={t('cardPosts.sentimentNegative')} onClick={() => handleSentiment('negative')}><BdsIcon theme="outline" name="emoji-negative" size="small" color={SENTIMENT_COLORS.negative} /></button>
                </BdsTooltip>
                <BdsTooltip position="right-center" tooltip-text={t('cardPosts.sentimentNeutral')}>
                  <button className="post-card__dropdown-item" aria-label={t('cardPosts.sentimentNeutral')} onClick={() => handleSentiment('neutral')}><BdsIcon theme="outline" name="emoji-neutral" size="small" color={SENTIMENT_COLORS.neutral} /></button>
                </BdsTooltip>
              </div>
            )}
          </div>

          {/* Check */}
          <BdsTooltip position="top-center" tooltip-text={t('cardPosts.check')}>
            <button
              className={`post-card__group-btn${isChecked ? ' post-card__group-btn--checked' : ''}`}
              aria-label={t('cardPosts.check')}
              onClick={handleCheck}
            >
              <BdsIcon theme="outline" name="checkball" size="small" />
            </button>
          </BdsTooltip>

          {/* View */}
          <BdsTooltip position="top-center" tooltip-text={t('cardPosts.view')}>
            <button className="post-card__group-btn" aria-label={t('cardPosts.view')} onClick={handleView}>
              <BdsIcon theme="outline" name="eye-open" size="small" />
            </button>
          </BdsTooltip>

          {/* Actions */}
          <div className="post-card__group-item">
            <BdsTooltip position="top-center" tooltip-text={t('cardPosts.actions')}>
              <button className="post-card__group-btn post-card__group-btn--wide" aria-label={t('cardPosts.actions')} onClick={() => setOpenDropdown((p) => (p === 'actions' ? null : 'actions'))}>
                <BdsIcon theme="outline" name={POST_CARD_ACTION_ICONS[actionsSelection]} size="small" />
                <BdsIcon theme="outline" name={openDropdown === 'actions' ? 'arrow-up' : 'arrow-down'} size="x-small" />
              </button>
            </BdsTooltip>
            {openDropdown === 'actions' && (
              <div className="post-card__dropdown">
                <BdsTooltip tooltip-text={t('cardPosts.copyClipboard')}>
                  <button className="post-card__dropdown-item" aria-label={t('cardPosts.copyId')} onClick={() => handleActions('copy-id')}><BdsIcon theme="outline" name="copy" size="small" /></button>
                </BdsTooltip>
                <BdsTooltip tooltip-text={t('cardPosts.copyLink')}>
                  <button className="post-card__dropdown-item" aria-label={t('cardPosts.copyLink')} onClick={() => handleActions('copy-link')}><BdsIcon theme="outline" name="link" size="small" /></button>
                </BdsTooltip>
                <BdsTooltip tooltip-text={t('cardPosts.copyDownload')}>
                  <button className="post-card__dropdown-item" aria-label={t('cardPosts.copyDownload')} onClick={() => handleActions('export')}><BdsIcon theme="outline" name="download" size="small" /></button>
                </BdsTooltip>
              </div>
            )}
          </div>

          {/* Delete */}
          <BdsTooltip position="top-center" tooltip-text={t('cardPosts.delete')}>
            <button className="post-card__group-btn" aria-label={t('cardPosts.delete')} onClick={handleDelete}>
              <BdsIcon theme="outline" name="trash" size="small" />
            </button>
          </BdsTooltip>

          {/* Ban user */}
          <BdsTooltip position="top-center" tooltip-text={t('cardPosts.banUser')}>
            <button className="post-card__group-btn post-card__group-btn--last" aria-label={t('cardPosts.banUser')} onClick={handleBanUser}>
              <BdsIcon theme="outline" name="false" size="small" />
            </button>
          </BdsTooltip>
        </div>
      </div>
    </>
  );
};
